import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Mail, MessageSquare, Brain, CreditCard, FileSpreadsheet,
  Plus, Settings, Trash2, Play, CheckCircle, XCircle, ExternalLink,
  RefreshCw, Key, Zap, Webhook, AlertCircle, Copy, Eye, EyeOff
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

// Integration icons mapping
const INTEGRATION_ICONS = {
  gmail: Mail,
  slack: MessageSquare,
  openai: Brain,
  stripe: CreditCard,
  sheets: FileSpreadsheet,
};

const INTEGRATION_COLORS = {
  gmail: "#EA4335",
  slack: "#4A154B",
  openai: "#10A37F",
  stripe: "#635BFF",
  sheets: "#0F9D58",
};

export default function IntegrationsPage() {
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [userIntegrations, setUserIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [configData, setConfigData] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Fetch available and user integrations
  const fetchIntegrations = useCallback(async () => {
    try {
      const [availableRes, userRes] = await Promise.all([
        axios.get(`${API}/integrations/available`),
        axios.get(`${API}/integrations/user`, { withCredentials: true })
      ]);
      setAvailableIntegrations(availableRes.data.integrations || []);
      setUserIntegrations(userRes.data.integrations || []);
    } catch (error) {
      console.error("Error fetching integrations:", error);
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleConnectOAuth = async (type) => {
    try {
      const response = await axios.get(`${API}/integrations/oauth/${type}/url`, { withCredentials: true });
      // Open OAuth popup
      window.open(response.data.url, '_blank', 'width=600,height=700');
      toast.info(`Opening ${type} authentication...`);
    } catch (error) {
      console.error("OAuth error:", error);
      toast.error("Failed to start OAuth flow");
    }
  };

  const handleConnectAPIKey = async (type) => {
    setSelectedType(type);
    setConfigData({});
    setShowConfigModal(true);
  };

  const handleSaveConfig = async () => {
    if (!selectedType) return;
    
    try {
      const existing = userIntegrations.find(i => i.integration_type === selectedType);
      
      if (existing) {
        await axios.put(`${API}/integrations/${existing.integration_id}`, {
          integration_type: selectedType,
          credentials: configData.credentials || {},
          settings: configData.settings || {}
        }, { withCredentials: true });
        toast.success("Integration updated");
      } else {
        await axios.post(`${API}/integrations`, {
          integration_type: selectedType,
          credentials: configData.credentials || {},
          settings: configData.settings || {}
        }, { withCredentials: true });
        toast.success("Integration connected");
      }
      
      setShowConfigModal(false);
      fetchIntegrations();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save integration");
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (!window.confirm("Are you sure you want to disconnect this integration?")) return;
    
    try {
      await axios.delete(`${API}/integrations/${integrationId}`, { withCredentials: true });
      toast.success("Integration disconnected");
      fetchIntegrations();
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleTest = async (integration) => {
    setSelectedIntegration(integration);
    setTestResult(null);
    setShowTestModal(true);
    setTesting(true);
    
    try {
      const response = await axios.post(
        `${API}/integrations/${integration.integration_id}/test`,
        {},
        { withCredentials: true }
      );
      setTestResult(response.data);
    } catch (error) {
      setTestResult({ success: false, error: error.response?.data?.detail || "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  const handleExecuteAction = async (integrationId, actionName, params) => {
    try {
      const response = await axios.post(
        `${API}/integrations/${integrationId}/execute`,
        { action_name: actionName, params },
        { withCredentials: true }
      );
      toast.success("Action executed successfully");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail?.error || "Action failed");
      throw error;
    }
  };

  const getIntegrationStatus = (type) => {
    return userIntegrations.find(i => i.integration_type === type);
  };

  const renderConfigForm = () => {
    if (!selectedType) return null;

    const integration = availableIntegrations.find(i => i.type === selectedType);
    if (!integration) return null;

    if (integration.auth_type === 'api_key') {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>API Key</Label>
            <div className="relative">
              <Input
                type={showApiKey ? "text" : "password"}
                value={configData.credentials?.api_key || ""}
                onChange={(e) => setConfigData({
                  ...configData,
                  credentials: { ...configData.credentials, api_key: e.target.value }
                })}
                placeholder={`Enter your ${integration.display_name} API key`}
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
            {selectedType === 'openai' && (
              <p className="text-xs text-muted-foreground">
                Get your API key from{" "}
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  OpenAI Dashboard <ExternalLink className="w-3 h-3 inline" />
                </a>
              </p>
            )}
            {selectedType === 'stripe' && (
              <p className="text-xs text-muted-foreground">
                Get your API key from{" "}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Stripe Dashboard <ExternalLink className="w-3 h-3 inline" />
                </a>
                . Use test keys (sk_test_...) for testing.
              </p>
            )}
          </div>
          
          {selectedType === 'stripe' && (
            <div className="space-y-2">
              <Label>Webhook Secret (Optional)</Label>
              <Input
                type="password"
                value={configData.settings?.webhook_secret || ""}
                onChange={(e) => setConfigData({
                  ...configData,
                  settings: { ...configData.settings, webhook_secret: e.target.value }
                })}
                placeholder="whsec_..."
              />
              <p className="text-xs text-muted-foreground">
                Required for receiving Stripe webhooks. Set this in Stripe Dashboard under Webhooks.
              </p>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="text-center py-4">
        <p className="text-muted-foreground mb-4">
          This integration uses OAuth authentication
        </p>
        <Button onClick={() => handleConnectOAuth(selectedType)}>
          <ExternalLink className="w-4 h-4 mr-2" />
          Connect with {integration.display_name}
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">Integrations</h1>
            <p className="text-muted-foreground">
              Connect your favorite apps to automate workflows
            </p>
          </div>
        </div>

        {/* Connected Integrations */}
        {userIntegrations.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Connected</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userIntegrations.map((integration) => {
                const Icon = INTEGRATION_ICONS[integration.integration_type] || Zap;
                const color = INTEGRATION_COLORS[integration.integration_type] || "#6B7280";
                
                return (
                  <Card key={integration.integration_id} className="relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: color }} />
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                            style={{ backgroundColor: color }}
                          >
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <CardTitle className="text-lg capitalize">
                              {integration.integration_type}
                            </CardTitle>
                            <Badge 
                              variant={integration.status === 'connected' ? 'default' : 'destructive'}
                              className="text-xs"
                            >
                              {integration.status === 'connected' ? (
                                <><CheckCircle className="w-3 h-3 mr-1" /> Connected</>
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" /> Error</>
                              )}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        Connected {new Date(integration.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleTest(integration)}
                        >
                          <Play className="w-4 h-4 mr-1" /> Test
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setSelectedType(integration.integration_type);
                            setConfigData({
                              credentials: integration.credentials,
                              settings: integration.settings
                            });
                            setShowConfigModal(true);
                          }}
                        >
                          <Settings className="w-4 h-4 mr-1" /> Settings
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDisconnect(integration.integration_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Available Integrations */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Available Integrations</h2>
          <Tabs defaultValue="all">
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="communication">Communication</TabsTrigger>
              <TabsTrigger value="ai">AI</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="productivity">Productivity</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableIntegrations.map((integration) => {
                  const Icon = INTEGRATION_ICONS[integration.type] || Zap;
                  const connected = getIntegrationStatus(integration.type);
                  
                  return (
                    <Card 
                      key={integration.type}
                      className={`transition-all hover:shadow-md ${connected ? 'opacity-60' : ''}`}
                    >
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-white shadow-sm"
                            style={{ backgroundColor: integration.color }}
                          >
                            <Icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-lg">{integration.display_name}</CardTitle>
                            <CardDescription className="text-xs">
                              {integration.triggers?.length || 0} triggers • {integration.actions?.length || 0} actions
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {/* Sample triggers/actions */}
                        <div className="space-y-2 mb-4">
                          {integration.triggers?.slice(0, 2).map((trigger, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Webhook className="w-3 h-3" />
                              <span>{trigger.display_name}</span>
                            </div>
                          ))}
                          {integration.actions?.slice(0, 2).map((action, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                              <Zap className="w-3 h-3" />
                              <span>{action.display_name}</span>
                            </div>
                          ))}
                        </div>
                        
                        {connected ? (
                          <Badge variant="secondary" className="w-full justify-center">
                            <CheckCircle className="w-3 h-3 mr-1" /> Connected
                          </Badge>
                        ) : (
                          <Button 
                            className="w-full"
                            onClick={() => {
                              if (integration.auth_type === 'oauth2') {
                                handleConnectOAuth(integration.type);
                              } else {
                                handleConnectAPIKey(integration.type);
                              }
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Connect
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
            
            {/* Other tabs would filter by category */}
            <TabsContent value="communication" className="mt-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableIntegrations
                  .filter(i => ['gmail', 'slack'].includes(i.type))
                  .map((integration) => {
                    const Icon = INTEGRATION_ICONS[integration.type] || Zap;
                    const connected = getIntegrationStatus(integration.type);
                    
                    return (
                      <Card key={integration.type}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                              style={{ backgroundColor: integration.color }}
                            >
                              <Icon className="w-6 h-6" />
                            </div>
                            <CardTitle>{integration.display_name}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {connected ? (
                            <Badge variant="secondary" className="w-full justify-center">
                              <CheckCircle className="w-3 h-3 mr-1" /> Connected
                            </Badge>
                          ) : (
                            <Button className="w-full" onClick={() => handleConnectOAuth(integration.type)}>
                              <Plus className="w-4 h-4 mr-2" /> Connect
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            </TabsContent>
            
            <TabsContent value="ai" className="mt-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableIntegrations
                  .filter(i => i.type === 'openai')
                  .map((integration) => {
                    const Icon = INTEGRATION_ICONS[integration.type] || Zap;
                    const connected = getIntegrationStatus(integration.type);
                    
                    return (
                      <Card key={integration.type}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                              style={{ backgroundColor: integration.color }}
                            >
                              <Icon className="w-6 h-6" />
                            </div>
                            <div>
                              <CardTitle>{integration.display_name}</CardTitle>
                              <CardDescription>GPT-4, Embeddings, Whisper</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {connected ? (
                            <Badge variant="secondary" className="w-full justify-center">
                              <CheckCircle className="w-3 h-3 mr-1" /> Connected
                            </Badge>
                          ) : (
                            <Button className="w-full" onClick={() => handleConnectAPIKey(integration.type)}>
                              <Key className="w-4 h-4 mr-2" /> Add API Key
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            </TabsContent>
            
            <TabsContent value="payments" className="mt-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableIntegrations
                  .filter(i => i.type === 'stripe')
                  .map((integration) => {
                    const Icon = INTEGRATION_ICONS[integration.type] || Zap;
                    const connected = getIntegrationStatus(integration.type);
                    
                    return (
                      <Card key={integration.type}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                              style={{ backgroundColor: integration.color }}
                            >
                              <Icon className="w-6 h-6" />
                            </div>
                            <div>
                              <CardTitle>{integration.display_name}</CardTitle>
                              <CardDescription>Payments, Invoices, Subscriptions</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {connected ? (
                            <Badge variant="secondary" className="w-full justify-center">
                              <CheckCircle className="w-3 h-3 mr-1" /> Connected
                            </Badge>
                          ) : (
                            <Button className="w-full" onClick={() => handleConnectAPIKey(integration.type)}>
                              <Key className="w-4 h-4 mr-2" /> Add API Key
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            </TabsContent>
            
            <TabsContent value="productivity" className="mt-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableIntegrations
                  .filter(i => i.type === 'sheets')
                  .map((integration) => {
                    const Icon = INTEGRATION_ICONS[integration.type] || Zap;
                    const connected = getIntegrationStatus(integration.type);
                    
                    return (
                      <Card key={integration.type}>
                        <CardHeader>
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                              style={{ backgroundColor: integration.color }}
                            >
                              <Icon className="w-6 h-6" />
                            </div>
                            <div>
                              <CardTitle>{integration.display_name}</CardTitle>
                              <CardDescription>Rows, Cells, Formatting</CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {connected ? (
                            <Badge variant="secondary" className="w-full justify-center">
                              <CheckCircle className="w-3 h-3 mr-1" /> Connected
                            </Badge>
                          ) : (
                            <Button className="w-full" onClick={() => handleConnectOAuth(integration.type)}>
                              <Plus className="w-4 h-4 mr-2" /> Connect
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Documentation */}
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-primary" />
              Setup Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold">1. Connect Integration</h4>
                <p className="text-sm text-muted-foreground">
                  Click Connect and follow the OAuth flow or enter your API key.
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">2. Configure Triggers</h4>
                <p className="text-sm text-muted-foreground">
                  Set up triggers in your workflows to start automation on events.
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold">3. Add Actions</h4>
                <p className="text-sm text-muted-foreground">
                  Add integration actions to your workflows to send data to connected apps.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Config Modal */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="capitalize flex items-center gap-2">
              {selectedType && INTEGRATION_ICONS[selectedType] && (
                (() => {
                  const Icon = INTEGRATION_ICONS[selectedType];
                  return <Icon className="w-5 h-5" style={{ color: INTEGRATION_COLORS[selectedType] }} />;
                })()
              )}
              Configure {selectedType}
            </DialogTitle>
            <DialogDescription>
              Enter your credentials to connect this integration
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {renderConfigForm()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancel</Button>
            <Button onClick={handleSaveConfig}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Modal */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Integration Test</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {testing ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-primary" />
                <span className="ml-3">Testing connection...</span>
              </div>
            ) : testResult ? (
              <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.success ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span className="font-semibold">
                    {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                  </span>
                </div>
                {testResult.error && (
                  <p className="text-sm text-red-500">{testResult.error}</p>
                )}
                {testResult.result && (
                  <pre className="text-xs bg-card p-2 rounded mt-2 overflow-auto max-h-40">
                    {JSON.stringify(testResult.result, null, 2)}
                  </pre>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowTestModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
